/**
 * Sticky product details that never trap content.
 *
 * Horizon's "Make details sticky on desktop" pins the details column under
 * the header. When accordions push the column taller than the screen, its
 * lower half sits out of view until the gallery has scrolled past, so the
 * wheel appears to move only the images.
 *
 * This script publishes the column height as `--ll-details-h`; the rule in
 * ll-custom.css turns it into a `top` that sticks under the header while the
 * column fits and pins its bottom edge once it does not. No nested scroll area.
 *
 * A ResizeObserver catches accordions, variant changes and late app content
 * (Judge.me, Infinite Options). A MutationObserver picks up details columns
 * that arrive later: product swaps morph `main`, and the theme editor
 * re-renders sections.
 */
const SELECTOR = '.product-details.sticky-content--desktop';

const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
    /** @type {HTMLElement} */ (entry.target).style.setProperty('--ll-details-h', `${Math.ceil(height)}px`);
  }
});

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

function observeAll() {
  for (const element of document.querySelectorAll(SELECTOR)) {
    if (observed.has(element)) continue;
    observed.add(element);
    resizeObserver.observe(element);
  }
}

let queued = false;
const mutationObserver = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    observeAll();
  });
});

observeAll();
mutationObserver.observe(document.body, { childList: true, subtree: true });
